// Shared vocabulary for the next-step order pipeline. The SERVER decides
// what the primary action is (order.next_action / order.cancel_action from
// GET /api/rentals/orders); these are display helpers only.
// Design: backend docs/DESIGN_NEXT_STEP_PIPELINE.md.
import { useEffect, useState } from 'react';

export const STATUSES = ['draft', 'reserved', 'started', 'returned', 'cancelled']; // = the DB check constraint (no 'archived')
export const STATUS_LABEL = { draft: 'Draft', reserved: 'Reserved', started: 'Out', returned: 'Returned', cancelled: 'Cancelled' };
export const STATUS_COLOR = {
  draft:     { bg: '#1e2235', color: '#e2e8f0', border: '#3a3f60' },
  reserved:  { bg: '#1a2a4a', color: '#60a5fa', border: '#1e3a6a' },
  started:   { bg: '#1a3a2a', color: '#34d399', border: '#1e4a35' },
  returned:  { bg: '#2a2a1a', color: '#fbbf24', border: '#3a3a1e' },
  cancelled: { bg: '#3a1a1a', color: '#f87171', border: '#4a2020' },
};
export const STAGES = ['draft', 'reserved', 'started', 'returned'];
export const ACTION_LABEL = { create: 'created', reserve: 'Reserve button', checkout: 'Check Out step', return: 'Return step', cancel: 'Cancel', manual: 'manual override', system: 'system' };

export const fmtMoney = (n) => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// A timestamp in the order's location time zone (falls back to the browser's).
export function fmtWhen(iso, tz) {
  if (!iso) return '—';
  const opts = { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' };
  try { return new Date(iso).toLocaleString('en-US', { ...(tz ? { timeZone: tz } : {}), ...opts }); }
  catch { return new Date(iso).toLocaleString('en-US', opts); }
}

// The latest event that reached `stage`, for the stage strip popover.
export function eventForStage(order, stage) {
  const evs = (order?.events || []).filter(e => e.to_status === stage);
  return evs.length ? evs[evs.length - 1] : null;
}

// Phone-width layout switch (the Return step and the detail page are mobile-first).
export function useIsNarrow(maxWidth = 640) {
  const q = `(max-width: ${maxWidth}px)`;
  const [narrow, setNarrow] = useState(() => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(q).matches : false));
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const m = window.matchMedia(q);
    const on = (e) => setNarrow(e.matches);
    m.addEventListener ? m.addEventListener('change', on) : m.addListener(on);
    return () => { m.removeEventListener ? m.removeEventListener('change', on) : m.removeListener(on); };
  }, [q]);
  return narrow;
}

// "Next: N going out today →" (design DESIGN_CHECKIN_CHECKOUT_PAGES.md §4): from the
// dashboard's goingOut rows (reserved pickups dated today at the default location),
// minus the order on screen; `next` is the earliest created (id tiebreak), the same
// order the dashboard card lists first.
export function nextGoingOut(rows, excludeId) {
  const list = (rows || []).filter(o => o.status === 'reserved' && o.id !== excludeId)
    .sort((a, b) => (a.created_at || '').localeCompare(b.created_at || '') || (a.id - b.id));
  return { count: list.length, next: list[0] || null };
}

export const isTouch = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

// Shared dark-theme bits for the dialogs and steps.
export const UI = {
  overlay: (narrow) => ({ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', zIndex: 4000, display: 'flex', alignItems: narrow ? 'stretch' : 'center', justifyContent: 'center', padding: narrow ? 0 : 20 }),
  box: (narrow, maxWidth = 480) => ({ background: '#252838', border: narrow ? 'none' : '1px solid #1a1d2e', borderRadius: narrow ? 0 : 14, width: '100%', maxWidth: narrow ? '100%' : maxWidth, height: narrow ? '100%' : 'auto', maxHeight: narrow ? '100%' : '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', boxSizing: 'border-box' }),
  head: { padding: '14px 18px', borderBottom: '1px solid #1a1d2e', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 },
  body: { padding: 18, overflowY: 'auto', flex: 1 },
  foot: { padding: '12px 18px', borderTop: '1px solid #1a1d2e', display: 'flex', gap: 10 },
  title: { fontWeight: 800, fontSize: 15, color: '#e2e8f0' },
  sub: { fontSize: 11, color: '#94a3b8', marginTop: 2 },
  lbl: { fontSize: 11, fontWeight: 600, color: '#e2e8f0', textTransform: 'uppercase', letterSpacing: '.4px', display: 'block', marginBottom: 6, marginTop: 14 },
  inp: { padding: '10px 12px', borderRadius: 8, border: '1px solid #2a3050', background: '#181928', color: '#e2e8f0', fontSize: 13, width: '100%', boxSizing: 'border-box' },
  row: { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: '1px solid #1a1d2e', fontSize: 12, color: '#cbd5e1' },
  effect: { fontSize: 12, color: '#94a3b8', padding: '4px 0', lineHeight: 1.5 },
  cancelBtn: { flex: 1, padding: 11, borderRadius: 8, border: '1px solid #2a3050', background: 'transparent', color: '#e2e8f0', cursor: 'pointer', fontSize: 13 },
  goBtn: (enabled, bg = '#14532d', color = '#4ade80') => ({ flex: 2, padding: 11, borderRadius: 8, border: 'none', background: enabled ? bg : '#1e2235', color: enabled ? color : '#64748b', fontWeight: 800, cursor: enabled ? 'pointer' : 'not-allowed', fontSize: 14 }),
  close: { background: 'none', border: 'none', color: '#e2e8f0', cursor: 'pointer', fontSize: 18, lineHeight: 1 },
};
